import { FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { CertificationInfo } from '@client/cart/components/AddToCart/CertificationInfo';
import { useCartQuantity } from '@client/cart/hooks/cartQuantity/useCartQuantity';
import emitter, { EVENT } from '@client/common/utils/eventEmitter';
import { useTranslate } from '@client/i18n/hooks';
import { Alert, ALERT_DISPLAY_TYPE, ALERT_STYLE_TYPE } from '@lib/components/Alert';
import { Button, BUTTON_ICON_POSITION, BUTTON_TYPE } from '@lib/components/Button';
import { ButtonGroup } from '@lib/components/ButtonGroup';
import { ValueType } from '@lib/components/Form/common';
import { CounterInput } from '@lib/components/Form/CounterInput';
import { Icon, ICON_NAME, ICON_SIZE } from '@lib/components/Icon';
import classNames from 'classnames';
import { Hint } from './Hint';
import { Props } from './';
import './Full.scss';

export const Full = ({
  className,
  dataAttributes,
  isBlocked,
  hasAccessCartPermissions,
  hasCertificationInfo,
  isLoggedIn,
  onAddToCart,
  settings,
  text,
  sku,
}: Props): ReactElement => {
  const t = useTranslate();
  useCartQuantity();
  const { items, setItemQuantity } = useCartQuantity();
  const prevSkuRef = useRef<string | null>(null);

  const {
    isBuyable,
    isSellout,
    quantity,
    isDisabledByCoreAssortment,
  } = settings;

  const defaultState = { currentQuantity: 1, attemptedQuantity: 1 };
  const [state, setState] = useState(() => {
    const savedItem = items[sku];
    return savedItem
      ? { currentQuantity: savedItem.quantity, attemptedQuantity: savedItem.attemptedQuantity }
      : defaultState;
  });

  useEffect(() => {
    if (prevSkuRef.current !== sku) {
      prevSkuRef.current = sku;
      const savedItem = items[sku];
      const newState = savedItem
        ? { currentQuantity: savedItem.quantity, attemptedQuantity: savedItem.attemptedQuantity }
        : defaultState;
      setState(newState);
    }
  }, [sku, items, defaultState]);

  useEffect(() => {
    const savedItem = items[sku];
    if (savedItem) {
      setState({
        currentQuantity: savedItem.quantity,
        attemptedQuantity: savedItem.attemptedQuantity,
      });
    }
  }, [items, sku]);

  const { currentQuantity, attemptedQuantity } = state;
  const counterId = 'cart/addToCart/full/counterId';
  const handleNumberChange = (value: ValueType, attemptedQuantity: ValueType) => {
    const newQuantity = +value || 1;
    const newAttemptedQuantity = +attemptedQuantity || 1;

    setState(prevState => ({
      ...prevState,
      currentQuantity: newQuantity,
      attemptedQuantity: newAttemptedQuantity,
    }));

    setItemQuantity(sku, newQuantity, newAttemptedQuantity);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    onAddToCart(state.currentQuantity);
  };

  const getAlertMessage = () => {
    switch (true) {
      case isDisabledByCoreAssortment:
        return text.notInCoreAssortment;
      case isBlocked:
        return text.blockedUser;
      case (isSellout && quantity <= 0):
        return text.isSoldOut;
      case (!isBuyable):
        return text.notBuyable;
      case (isLoggedIn && !hasAccessCartPermissions):
        return text.notPermitted;
      default:
        return undefined;
    }
  };

  const isDisabled = isBlocked
    || (isSellout && quantity <= 0)
    || !isBuyable
    || (isLoggedIn && !hasAccessCartPermissions)
    || hasCertificationInfo
    || isDisabledByCoreAssortment;

  const isAlertVisible = isBlocked
    || (isSellout && quantity <= 0)
    || !isBuyable
    || (isLoggedIn && !hasAccessCartPermissions)
    || isDisabledByCoreAssortment;

  useEffect(() => {
    const handleAddToCartFulfilled = () => {
      setState(defaultState);
      setItemQuantity(sku, 1, 1);
    };

    emitter.on(EVENT.addToCartFulfilled, handleAddToCartFulfilled);

    return () => {
      emitter.off(EVENT.addToCartFulfilled);
    };
  }, [sku, setItemQuantity, defaultState]);

  return (
    <form className={className} onSubmit={handleSubmit}>
      <ButtonGroup className={classNames({ 'FullAddToCart-ButtonGroup--WithCertification': hasCertificationInfo })}>
        <CounterInput
          counterId={counterId}
          value={currentQuantity}
          onChange={handleNumberChange}
          isDisabled={isDisabled}
          maxValue={999}
        />
        <Button
          isDisabled={isDisabled}
          iconPosition={BUTTON_ICON_POSITION.left}
          type={BUTTON_TYPE.submit}
          dataAttributes={dataAttributes}
        >
          <Icon name={ICON_NAME.cart} size={ICON_SIZE.medium} />
          {t('Add to cart', {}, 'Buy button label')}
        </Button>
      </ButtonGroup>

      {hasCertificationInfo && <CertificationInfo className={className} />}

      {isAlertVisible && (
        <Alert displayType={ALERT_DISPLAY_TYPE.text} styleType={ALERT_STYLE_TYPE.info}>
          {getAlertMessage()}
        </Alert>
      )}

      <Hint
        attemptedQuantity={attemptedQuantity}
        currentQuantity={currentQuantity}
        settings={settings}
        text={text}
      />
    </form>
  );
};
